/**
 * NavItem - Sidebar nav row with a 20px icon slot, label, and optional
 * notification badge. Supports active / hover states and a click handler.
 */

import { BaseUIComponent } from './BaseUIComponent.js';
import { NotificationBadge } from './NotificationBadge.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface NavItemData {
  /** Visible label text. */
  label: string;
  /** Optional inline SVG / HTML for the leading icon. */
  iconHtml?: string;
  /** Whether the item is currently active / selected. */
  active?: boolean;
  /** Notification badge count. 0 or negative hides the badge. */
  badgeCount?: number;
  /** Click handler invoked on activation. */
  onClick?: (event: MouseEvent) => void;
}

export interface NavItemOptions extends BaseUIComponentOptions, NavItemData {}

export class NavItem extends BaseUIComponent<NavItemOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Nav Item',
    category: 'navigation',
    summary: 'Sidebar nav row with icon, label, optional notification badge, and active state.',
    useCases: ['sidebar', 'nav row', 'menu entry', 'tab list item'],
    optionsType: 'NavItemOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/nav-item.html',
      styles: '@engine/assets/ui/styles/nav-item.css',
    },
  };

  private rootElement: HTMLButtonElement | null = null;
  private iconElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;
  private badgeSlot: HTMLElement | null = null;
  private badge: NotificationBadge | null = null;
  private clickHandler: ((event: MouseEvent) => void) | null = null;

  protected override getAssetPaths() {
    return {
      templatePath: NavItem.metadata.assetPaths.template,
      stylesPath: NavItem.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<NavItemOptions> {
    return {
      position: 'center',
      visible: true,
      customClasses: [],
      customStyles: {},
      label: 'Nav Item',
      iconHtml: '',
      active: false,
      badgeCount: 0,
      onClick: () => { /* no-op */ },
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      label: this.options.label,
      iconHtml: this.options.iconHtml,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-nav-item') as HTMLButtonElement | null;
    this.iconElement = this.layout.querySelector('[data-nav-item-icon]') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-nav-item-label]') as HTMLElement | null;
    this.badgeSlot = this.layout.querySelector('[data-nav-item-badge]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    if (this.badgeSlot) {
      this.badge = await this.mountChild(
        NotificationBadge,
        { mode: 'count', count: Math.max(0, this.options.badgeCount) },
        this.badgeSlot,
      );
    }
    this.applyActive();
    this.applyBadge();
    this.attachClickListener();
  }

  private attachClickListener(): void {
    if (!this.rootElement) return;
    this.clickHandler = (event: MouseEvent) => {
      this.options.onClick(event);
    };
    this.rootElement.addEventListener('click', this.clickHandler);
  }

  /** Replace the nav item data. Unspecified fields fall back to defaults. */
  public setItem(item: NavItemData): void {
    this.options.label = item.label;
    this.options.iconHtml = item.iconHtml ?? '';
    this.options.active = item.active ?? false;
    this.options.badgeCount = item.badgeCount ?? 0;
    if (item.onClick) this.options.onClick = item.onClick;

    if (this.labelElement) this.labelElement.textContent = this.options.label;
    if (this.iconElement) this.iconElement.innerHTML = this.options.iconHtml;
    this.applyActive();
    this.applyBadge();
  }

  /** Update the active state. */
  public setActive(active: boolean): void {
    this.options.active = active;
    this.applyActive();
  }

  /** Update the notification badge count. 0 or negative hides the badge. */
  public setBadgeCount(count: number): void {
    this.options.badgeCount = count;
    this.applyBadge();
  }

  /** Replace the click handler. */
  public setOnClick(handler: (event: MouseEvent) => void): void {
    this.options.onClick = handler;
  }

  private applyActive(): void {
    if (!this.rootElement) return;
    this.rootElement.dataset.active = this.options.active ? 'true' : 'false';
  }

  private applyBadge(): void {
    if (!this.rootElement) return;
    const count = this.options.badgeCount;
    const has = !!count && count > 0;
    this.rootElement.dataset.hasBadge = has ? 'true' : 'false';
    this.badge?.setCount(has ? count : 0);
  }

  protected override onDestroy(): void {
    if (this.rootElement && this.clickHandler) {
      this.rootElement.removeEventListener('click', this.clickHandler);
    }
    this.clickHandler = null;
    // badge is destroyed by BaseUIComponent.destroy() via mountChild tracking.
    this.badge = null;
    this.rootElement = null;
    this.iconElement = null;
    this.labelElement = null;
    this.badgeSlot = null;
  }
}
